/* global ImageData, File, Image */
/**
 * Raster image editor — ported from compuzz-editor (Angular).
 *
 * Opens as a modal Windows.open() dialog. Operates on a working canvas
 * (the editable raster, kept at the image's natural pixel size). A separate
 * display canvas mirrors the working canvas, scaled by `fitScale * zoomFactor`
 * to fit the viewport.
 *
 * Static API:
 *   ImageEditorView.open({
 *     url: <string>,           // image URL to load
 *     fileName: <string>,      // suggested file name (extension is replaced with .png)
 *     title: <string>,         // optional window title
 *     onSave: function(file)   // called with a PNG File when the user clicks save
 *   });
 */
define('imageEditorView',[
  'module',
  'backbone',
  'marionette',
  'underscore',
  'template!imageEditorView'
], function (
  Module,
  Backbone,
  Marionette,
  _,
  Tpl
) {
  'use strict';

  var View = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'image-editor-view',

    ui: {
      displayCanvas: '.ie-display-canvas',
      canvasHost: '.ie-canvas-host',
      canvasArea: '.ie-canvas-area',
      cropOverlay: '.ie-crop-overlay',
      cropConfirmBar: '.ie-crop-confirm-bar',
      zoomLabel: '.ie-zoom-label',
      tools: '.ie-tool[data-tool]',
      palette: '.ie-palette',
      paletteSection: '.ie-panel-palette',
      replaceErasePanel: '.ie-panel-replace-erase',
      cropPanel: '.ie-panel-crop',
      colorReducePanel: '.ie-panel-color-reduce',
      colorReduceList: '.ie-cr-list',
      colorReduceEmpty: '.ie-cr-empty',
      replaceTitle: '.ie-rep-title',
      eraseTitle: '.ie-erase-title',
      replaceHint: '.ie-replace-hint',
      eraseHint: '.ie-erase-hint',
      replaceColorRow: '.ie-rep-color-row',
      replaceColor: '.ie-replace-color',
      replaceHex: '.ie-replace-hex',
      tolerance: '.ie-tolerance',
      wandPanel: '.ie-panel-wand',
      wandTitle: '.ie-wand-title',
      lassoTitle: '.ie-lasso-title',
      wandTolRow: '.ie-wand-tol-row',
      wandTolerance: '.ie-wand-tolerance',
      wandHintsWand: '.ie-wand-hints-wand',
      wandHintsLasso: '.ie-wand-hints-lasso',
      wandActions: '.ie-wand-actions',
      wandColor: '.ie-wand-color',
      wandMultiply: '.ie-wand-multiply',
      wandEmpty: '.ie-wand-empty',
      brightness: '.ie-brightness',
      brightnessValue: '.ie-brightness-value',
      contrast: '.ie-contrast',
      contrastValue: '.ie-contrast-value',
      bakeBtn: '.ie-bake-btn'
    },

    events: {
      'click .ie-tool[data-tool]': 'onToolClick',
      'click .ie-tool[data-action="rotate-left"]': function () { this.rotate(-90); },
      'click .ie-tool[data-action="rotate-right"]': function () { this.rotate(90); },
      'click .ie-tool[data-action="flip-h"]': function () { this.flip('h'); },
      'click .ie-tool[data-action="flip-v"]': function () { this.flip('v'); },
      'click .ie-tool[data-action="auto-remove-bg"]': 'removeBackgroundAuto',
      'click .ie-tool[data-action="undo"]': 'undo',
      'click [data-action="zoom-in"]': 'zoomIn',
      'click [data-action="zoom-out"]': 'zoomOut',
      'click [data-action="zoom-reset"]': 'zoomReset',
      'click [data-action="apply-crop"]': 'applyCrop',
      'click [data-action="commit-adjustments"]': 'commitAdjustments',
      'click [data-action="cancel"]': 'cancel',
      'click [data-action="save"]': 'save',
      'input @ui.tolerance': function () { this.tolerance = parseInt(this.ui.tolerance.val(), 10) || 0; },
      'input @ui.replaceHex': 'onReplaceHexInput',
      'change @ui.replaceColor': function () { this.ui.replaceHex.val((this.ui.replaceColor.val() || '').toUpperCase()); },
      'input @ui.wandTolerance': function () { this.tolerance = parseInt(this.ui.wandTolerance.val(), 10) || 0; },
      'click .ie-wand-delete': 'wandDeleteSelection',
      'click .ie-wand-apply': 'wandApplyReplace',
      'input @ui.brightness': 'onBrightnessChange',
      'input @ui.contrast': 'onContrastChange',
      'mousedown @ui.displayCanvas': 'onCanvasMouseDown'
    },

    initialize: function (options) {
      this.options = options || {};

      this.activeTool = 'none';
      this.imageLoaded = false;
      this.zoomFactor = 1;
      this.fitScale = 1;
      this.tolerance = 20;
      this.brightness = 0;
      this.contrast = 0;
      this.undoStack = [];
      this.maxUndo = 20;
      this.adjustBaseline = null;
      this.colors = [];

      this.workingCanvas = null;
      this.workingCtx = null;

      // Magic wand / lasso selection (shared mask + blue overlay)
      this.selectionMask = null;          // Uint8Array, working-canvas sized
      this.selectionOverlayCanvas = null; // cached blue overlay, rebuilt on mask change
      this.lassoDrawing = false;
      this.lassoPoints = [];
      this.lassoMode = 'new';             // 'new' | 'add' | 'subtract'
      this.lassoOverlayCanvas = null;     // dedicated preview layer (flicker-free)
      this.lassoRafId = null;             // requestAnimationFrame throttle handle
    },

    onShow: function () {
      this.displayCanvas = this.ui.displayCanvas[0];
      this.displayCtx = this.displayCanvas.getContext('2d');

      // Wheel-to-zoom on the canvas area
      this._wheelHandler = _.bind(function (e) {
        if (!this.imageLoaded) return;
        e.preventDefault();
        if (e.deltaY < 0) this.zoomIn();
        else this.zoomOut();
      }, this);
      this.ui.canvasArea[0].addEventListener('wheel', this._wheelHandler, { passive: false });

      // Drag handlers for the crop overlay
      this._docMouseMove = _.bind(this.onDocMouseMove, this);
      this._docMouseUp = _.bind(this.onDocMouseUp, this);
      $(document).on('mousemove', this._docMouseMove);
      $(document).on('mouseup', this._docMouseUp);
      this.ui.cropOverlay.on('mousedown', _.bind(this.onCropOverlayMouseDown, this));

      // Delete / Backspace clears the wand/lasso selection's pixels.
      this._keydownHandler = _.bind(function (e) {
        var tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        var isDelete = e.key === 'Delete' || e.key === 'Backspace';
        if (isDelete && (this.activeTool === 'wand' || this.activeTool === 'lasso') && this.selectionMask) {
          e.preventDefault();
          this.wandDeleteSelection();
        }
      }, this);
      document.addEventListener('keydown', this._keydownHandler);

      this.loadImage(this.options.url);
    },

    onDestroy: function () {
      if (this._wheelHandler && this.ui.canvasArea && this.ui.canvasArea[0]) {
        this.ui.canvasArea[0].removeEventListener('wheel', this._wheelHandler);
      }
      $(document).off('mousemove', this._docMouseMove);
      $(document).off('mouseup', this._docMouseUp);
      if (this._keydownHandler) document.removeEventListener('keydown', this._keydownHandler);
      this.clearLassoPreview();
    },

    /* ------------------------------------------------------------------ */
    /* Image loading                                                       */
    /* ------------------------------------------------------------------ */

    loadImage: function (url) {
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = _.bind(function () {
        this.workingCanvas = document.createElement('canvas');
        this.workingCanvas.width = image.naturalWidth;
        this.workingCanvas.height = image.naturalHeight;
        this.workingCtx = this.workingCanvas.getContext('2d', { willReadFrequently: true });
        this.workingCtx.drawImage(image, 0, 0);
        this.colors = this.extractPalette(
          this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height),
          12
        );
        this.imageLoaded = true;
        this.renderPalette();
        this.fitCanvasToImage();
        this.renderWorkingToDisplay();
      }, this);
      image.onerror = function () { console.error('ImageEditor: failed to load image', url); };
      image.src = url;
    },

    /* ------------------------------------------------------------------ */
    /* Display rendering                                                   */
    /* ------------------------------------------------------------------ */

    renderWorkingToDisplay: function () {
      if (!this.workingCanvas) return;
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var scale = this.fitScale * this.zoomFactor;
      this.displayCanvas.width = w;
      this.displayCanvas.height = h;
      this.displayCanvas.style.width = (w * scale) + 'px';
      this.displayCanvas.style.height = (h * scale) + 'px';
      this.displayCtx.clearRect(0, 0, w, h);
      this.displayCtx.drawImage(this.workingCanvas, 0, 0);
      // The wand/lasso selection mask is working-canvas sized, so it overlays
      // the display canvas (same pixel dimensions) directly.
      if (this.selectionOverlayCanvas) {
        this.displayCtx.drawImage(this.selectionOverlayCanvas, 0, 0);
      }
      if (this.ui.zoomLabel) {
        this.ui.zoomLabel.text(Math.round(scale * 100) + '%');
      }
      // Keep the crop overlay aligned if it's active.
      if (this.activeTool === 'crop' && this.cropRect) {
        this.updateCropOverlayCss();
      }
    },

    fitCanvasToImage: function () {
      var area = this.ui.canvasArea[0];
      var maxW = (area ? area.clientWidth : 600) - 48;
      var maxH = (area ? area.clientHeight : 400) - 72;
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      this.fitScale = Math.min(maxW / w, maxH / h, 1);
      if (!isFinite(this.fitScale) || this.fitScale <= 0) this.fitScale = 1;
      this.zoomFactor = 1;
    },

    zoomIn: function () {
      this.zoomFactor = Math.min(this.zoomFactor * 1.25, 8);
      this.renderWorkingToDisplay();
    },

    zoomOut: function () {
      this.zoomFactor = Math.max(this.zoomFactor / 1.25, 0.1);
      this.renderWorkingToDisplay();
    },

    zoomReset: function () {
      this.zoomFactor = 1;
      this.renderWorkingToDisplay();
    },

    /* ------------------------------------------------------------------ */
    /* Snapshot / undo                                                     */
    /* ------------------------------------------------------------------ */

    snapshot: function () {
      if (!this.workingCtx) return;
      this.undoStack.push(this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height));
      if (this.undoStack.length > this.maxUndo) this.undoStack.shift();
    },

    undo: function () {
      var prev = this.undoStack.pop();
      if (!prev) return;
      if (prev.width !== this.workingCanvas.width || prev.height !== this.workingCanvas.height) {
        this.workingCanvas.width = prev.width;
        this.workingCanvas.height = prev.height;
      }
      this.workingCtx.putImageData(prev, 0, 0);
      this.adjustBaseline = null;
      this.brightness = 0;
      this.contrast = 0;
      this.ui.brightness.val(0);
      this.ui.contrast.val(0);
      this.ui.brightnessValue.text('0');
      this.ui.contrastValue.text('0');
      this.ui.bakeBtn.hide();
      this.fitCanvasToImage();
      this.renderWorkingToDisplay();
    },

    /* ------------------------------------------------------------------ */
    /* Palette                                                             */
    /* ------------------------------------------------------------------ */

    extractPalette: function (imageData, maxColors) {
      var data = imageData.data;
      var buckets = {};
      var step = 16;
      for (var i = 0; i < data.length; i += step) {
        if (data[i + 3] < 16) continue;
        var r = data[i] & 0xF0;
        var g = data[i + 1] & 0xF0;
        var b = data[i + 2] & 0xF0;
        var key = r + ',' + g + ',' + b;
        buckets[key] = (buckets[key] || 0) + 1;
      }
      var entries = [];
      for (var k in buckets) {
        if (buckets.hasOwnProperty(k)) entries.push([k, buckets[k]]);
      }
      entries.sort(function (a, b) { return b[1] - a[1]; });
      return entries.slice(0, maxColors).map(_.bind(function (entry) {
        var parts = entry[0].split(',').map(Number);
        return { hexaValue: this.rgbToHex(parts[0], parts[1], parts[2]) };
      }, this));
    },

    renderPalette: function () {
      if (!this.colors || !this.colors.length) {
        this.ui.paletteSection.hide();
        return;
      }
      this.ui.paletteSection.css('display', 'flex');
      var html = this.colors.map(function (c) {
        return '<button type="button" class="ie-swatch" style="background-color:' + c.hexaValue +
          '" data-color="' + c.hexaValue + '" title="' + c.hexaValue + '"></button>';
      }).join('');
      this.ui.palette.html(html);
      this.ui.palette.off('click').on('click', '.ie-swatch', _.bind(this.onPaletteClick, this));
    },

    onPaletteClick: function (e) {
      var hex = $(e.currentTarget).data('color');
      if (this.activeTool === 'replace') {
        this.snapshot();
        this.replaceColorInPlace(this.hexToRgb(hex), this.hexToRgb(this.ui.replaceColor.val()), this.tolerance);
        this.renderWorkingToDisplay();
      } else if (this.activeTool === 'erase') {
        this.snapshot();
        this.eraseColorInPlace(this.hexToRgb(hex), this.tolerance);
        this.renderWorkingToDisplay();
      } else if (this.activeTool === 'wand' || this.activeTool === 'lasso') {
        this.ui.wandColor.val(hex);
      } else {
        this.ui.replaceColor.val(hex);
        this.ui.replaceHex.val((hex || '').toUpperCase());
      }
    },

    // Lets the user type/paste a #RRGGBB value for the replace color instead of
    // going through the native colour dialog. Mirrors the validated hex into
    // the colour swatch so the two controls stay in sync.
    onReplaceHexInput: function () {
      var val = (this.ui.replaceHex.val() || '').trim();
      if (val.charAt(0) !== '#') val = '#' + val;
      if (/^#[0-9a-fA-F]{6}$/.test(val)) {
        this.ui.replaceColor.val(val.toLowerCase());
      }
    },

    /* ------------------------------------------------------------------ */
    /* Color helpers                                                       */
    /* ------------------------------------------------------------------ */

    hexToRgb: function (hex) {
      hex = (hex || '#000000').replace(/^#/, '');
      if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
      return {
        r: parseInt(hex.substr(0, 2), 16),
        g: parseInt(hex.substr(2, 2), 16),
        b: parseInt(hex.substr(4, 2), 16)
      };
    },

    rgbToHex: function (r, g, b) {
      var toHex = function (n) {
        var s = n.toString(16);
        return s.length === 1 ? '0' + s : s;
      };
      return '#' + toHex(r) + toHex(g) + toHex(b);
    },

    /* ------------------------------------------------------------------ */
    /* Tool selection                                                      */
    /* ------------------------------------------------------------------ */

    onToolClick: function (e) {
      var tool = $(e.currentTarget).data('tool');
      this.setTool(tool);
    },

    setTool: function (tool) {
      // Tear down any per-tool state
      this.endCrop();
      this.ui.colorReduceList.empty();
      this.cleanupLasso();
      this.clearSelection();

      if (this.activeTool === tool) {
        this.activeTool = 'none';
      } else {
        this.activeTool = tool;
      }

      var isWandLasso = this.activeTool === 'wand' || this.activeTool === 'lasso';

      // Toolbar highlights
      this.ui.tools.removeClass('active');
      if (this.activeTool !== 'none') {
        this.ui.tools.filter('[data-tool="' + this.activeTool + '"]').addClass('active');
      }

      // Side-panel visibility
      this.ui.cropPanel.toggle(this.activeTool === 'crop');
      this.ui.replaceErasePanel.toggle(this.activeTool === 'replace' || this.activeTool === 'erase');
      this.ui.replaceTitle.toggle(this.activeTool === 'replace');
      this.ui.eraseTitle.toggle(this.activeTool === 'erase');
      this.ui.replaceHint.toggle(this.activeTool === 'replace');
      this.ui.eraseHint.toggle(this.activeTool === 'erase');
      this.ui.replaceColorRow.toggle(this.activeTool === 'replace');
      this.ui.colorReducePanel.toggle(this.activeTool === 'colorReduce');
      this.ui.wandPanel.toggle(isWandLasso);
      this.ui.canvasHost.toggleClass('cursor-pick',
        this.activeTool === 'replace' || this.activeTool === 'erase' || isWandLasso);

      if (this.activeTool === 'crop') {
        this.startCrop();
      } else if (this.activeTool === 'colorReduce') {
        this.generateColorReducePreviews();
      } else if (isWandLasso) {
        this.updateWandPanel();
      }
    },

    /* ------------------------------------------------------------------ */
    /* Click sampling for replace/erase                                    */
    /* ------------------------------------------------------------------ */

    onCanvasMouseDown: function (e) {
      if (!this.workingCtx) return;
      if (this.activeTool === 'wand') { this.onWandClick(e); return; }
      if (this.activeTool === 'lasso') { this.onLassoMouseDown(e); return; }
      if (this.activeTool !== 'replace' && this.activeTool !== 'erase') return;
      var p = this.imageCoordsFromEvent(e);
      var x = Math.floor(p.x);
      var y = Math.floor(p.y);
      if (x < 0 || y < 0 || x >= this.workingCanvas.width || y >= this.workingCanvas.height) return;
      var pixel = this.workingCtx.getImageData(x, y, 1, 1).data;
      var sample = { r: pixel[0], g: pixel[1], b: pixel[2] };
      this.snapshot();
      if (this.activeTool === 'replace') {
        this.replaceColorInPlace(sample, this.hexToRgb(this.ui.replaceColor.val()), this.tolerance);
      } else {
        this.eraseColorInPlace(sample, this.tolerance);
      }
      this.renderWorkingToDisplay();
    },

    /* ------------------------------------------------------------------ */
    /* Pixel ops                                                           */
    /* ------------------------------------------------------------------ */

    replaceColorInPlace: function (oldRgb, newRgb, tolerance) {
      var imgData = this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height);
      var data = imgData.data;
      var tol2 = tolerance * tolerance * 3;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        var dr = data[i] - oldRgb.r;
        var dg = data[i + 1] - oldRgb.g;
        var db = data[i + 2] - oldRgb.b;
        if (dr * dr + dg * dg + db * db <= tol2) {
          data[i] = newRgb.r;
          data[i + 1] = newRgb.g;
          data[i + 2] = newRgb.b;
        }
      }
      this.workingCtx.putImageData(imgData, 0, 0);
    },

    eraseColorInPlace: function (target, tolerance) {
      var imgData = this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height);
      var data = imgData.data;
      var tol2 = tolerance * tolerance * 3;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        var dr = data[i] - target.r;
        var dg = data[i + 1] - target.g;
        var db = data[i + 2] - target.b;
        if (dr * dr + dg * dg + db * db <= tol2) {
          data[i + 3] = 0;
        }
      }
      this.workingCtx.putImageData(imgData, 0, 0);
    },

    removeBackgroundAuto: function () {
      if (!this.workingCtx) return;
      this.snapshot();
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var samples = [
        this.workingCtx.getImageData(0, 0, 1, 1).data,
        this.workingCtx.getImageData(w - 1, 0, 1, 1).data,
        this.workingCtx.getImageData(0, h - 1, 1, 1).data,
        this.workingCtx.getImageData(w - 1, h - 1, 1, 1).data
      ];
      for (var i = 0; i < samples.length; i++) {
        var s = samples[i];
        this.eraseColorInPlace({ r: s[0], g: s[1], b: s[2] }, this.tolerance);
      }
      this.renderWorkingToDisplay();
    },

    /* ------------------------------------------------------------------ */
    /* Magic wand / lasso selection                                        */
    /* ------------------------------------------------------------------ */

    // Working-canvas (image-pixel) coordinates for a mouse event. The display
    // canvas keeps the image's natural pixel size and is scaled via CSS, so we
    // map through its on-screen bounding rect.
    imageCoordsFromEvent: function (e) {
      var rect = this.displayCanvas.getBoundingClientRect();
      var sx = this.workingCanvas.width / rect.width;
      var sy = this.workingCanvas.height / rect.height;
      return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
    },

    // BFS flood fill from (startX, startY); returns a 0/255 mask the size of
    // the working canvas. Typed-array queue keeps it fast on large images.
    floodFill: function (startX, startY, tolerance) {
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var data = this.workingCtx.getImageData(0, 0, w, h).data;
      var mask = new Uint8Array(w * h);
      var startIdx = startY * w + startX;
      var sp = startIdx * 4;
      var sr = data[sp], sg = data[sp + 1], sb = data[sp + 2];
      var tol2 = tolerance * tolerance * 3;

      var queue = new Int32Array(w * h);
      var visited = new Uint8Array(w * h);
      var head = 0, tail = 0;
      visited[startIdx] = 1;
      queue[tail++] = startIdx;

      while (head < tail) {
        var idx = queue[head++];
        var pi = idx * 4;
        var dr = data[pi] - sr, dg = data[pi + 1] - sg, db = data[pi + 2] - sb;
        if (dr * dr + dg * dg + db * db > tol2) continue;
        mask[idx] = 255;
        var x = idx % w, y = (idx / w) | 0;
        if (x > 0     && !visited[idx - 1]) { visited[idx - 1] = 1; queue[tail++] = idx - 1; }
        if (x < w - 1 && !visited[idx + 1]) { visited[idx + 1] = 1; queue[tail++] = idx + 1; }
        if (y > 0     && !visited[idx - w]) { visited[idx - w] = 1; queue[tail++] = idx - w; }
        if (y < h - 1 && !visited[idx + w]) { visited[idx + w] = 1; queue[tail++] = idx + w; }
      }
      return mask;
    },

    updateSelectionOverlay: function () {
      if (!this.selectionMask) {
        this.selectionOverlayCanvas = null;
        this.renderWorkingToDisplay();
        return;
      }
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var oc = document.createElement('canvas');
      oc.width = w; oc.height = h;
      var ctx = oc.getContext('2d');
      var imgData = ctx.createImageData(w, h);
      var d = imgData.data;
      for (var i = 0; i < this.selectionMask.length; i++) {
        if (this.selectionMask[i]) {
          d[i * 4]     = 59;   // R
          d[i * 4 + 1] = 130;  // G
          d[i * 4 + 2] = 246;  // B  (blue-500)
          d[i * 4 + 3] = 110;  // A
        }
      }
      ctx.putImageData(imgData, 0, 0);
      this.selectionOverlayCanvas = oc;
      this.updateWandActions();
      this.renderWorkingToDisplay();
    },

    clearSelection: function () {
      this.selectionMask = null;
      this.selectionOverlayCanvas = null;
      this.updateWandActions();
      if (this.workingCanvas) this.renderWorkingToDisplay();
    },

    onWandClick: function (e) {
      var p = this.imageCoordsFromEvent(e);
      var x = Math.floor(p.x);
      var y = Math.floor(p.y);
      if (x < 0 || y < 0 || x >= this.workingCanvas.width || y >= this.workingCanvas.height) return;

      var newMask = this.floodFill(x, y, this.tolerance);
      var shift = e.shiftKey, alt = e.altKey;
      var i;
      if (!this.selectionMask || (!shift && !alt)) {
        this.selectionMask = newMask;
      } else if (shift && !alt) {
        for (i = 0; i < this.selectionMask.length; i++) { if (newMask[i]) this.selectionMask[i] = 255; }
      } else if (shift && alt) {
        for (i = 0; i < this.selectionMask.length; i++) { if (newMask[i]) this.selectionMask[i] = 0; }
      }
      this.updateSelectionOverlay();
    },

    // Punch true transparency into the selected pixels via destination-out
    // compositing — avoids premultiplied-alpha colour fringes.
    wandDeleteSelection: function () {
      if (!this.selectionMask) return;
      this.snapshot();
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var maskCanvas = document.createElement('canvas');
      maskCanvas.width = w; maskCanvas.height = h;
      var maskCtx = maskCanvas.getContext('2d');
      var maskData = maskCtx.createImageData(w, h);
      for (var i = 0; i < this.selectionMask.length; i++) {
        if (this.selectionMask[i]) maskData.data[i * 4 + 3] = 255;
      }
      maskCtx.putImageData(maskData, 0, 0);
      this.workingCtx.globalCompositeOperation = 'destination-out';
      this.workingCtx.drawImage(maskCanvas, 0, 0);
      this.workingCtx.globalCompositeOperation = 'source-over';
      this.adjustBaseline = null;
      this.clearSelection();
    },

    wandApplyReplace: function () {
      if (!this.selectionMask) return;
      this.snapshot();
      var rgb = this.hexToRgb(this.ui.wandColor.val());
      var multiply = this.ui.wandMultiply.is(':checked');
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var imgData = this.workingCtx.getImageData(0, 0, w, h);
      var d = imgData.data;
      for (var i = 0; i < this.selectionMask.length; i++) {
        if (!this.selectionMask[i]) continue;
        var pi = i * 4;
        if (multiply) {
          // Multiply blend keeps the original shading/texture under the colour.
          d[pi]     = Math.round(d[pi]     * rgb.r / 255);
          d[pi + 1] = Math.round(d[pi + 1] * rgb.g / 255);
          d[pi + 2] = Math.round(d[pi + 2] * rgb.b / 255);
        } else {
          d[pi] = rgb.r; d[pi + 1] = rgb.g; d[pi + 2] = rgb.b;
        }
      }
      this.workingCtx.putImageData(imgData, 0, 0);
      this.adjustBaseline = null;
      this.clearSelection();
    },

    onLassoMouseDown: function (e) {
      e.preventDefault();
      this.lassoDrawing = true;
      this.lassoMode = (e.shiftKey && e.altKey) ? 'subtract' : (e.shiftKey ? 'add' : 'new');
      this.lassoPoints = [this.imageCoordsFromEvent(e)];
    },

    onLassoMouseMove: function (e) {
      if (!this.lassoDrawing) return;
      var p = this.imageCoordsFromEvent(e);
      var last = this.lassoPoints[this.lassoPoints.length - 1];
      var dx = p.x - last.x, dy = p.y - last.y;
      if (dx * dx + dy * dy < 4) return; // skip < 2px moves
      this.lassoPoints.push(p);
      this.drawLassoPreview();
    },

    // Lazily creates a dedicated overlay canvas stacked over the display canvas.
    // Drawing the in-progress polygon here (rather than on the display canvas)
    // keeps the preview flicker-free — the image underneath is never repainted
    // while dragging. Internal size matches the working canvas, so path points
    // stay in image-pixel space; CSS scaling maps it onto the display box.
    getLassoOverlayCtx: function () {
      if (!this.lassoOverlayCanvas) {
        var host = this.ui.canvasHost[0];
        if (!host) return null;
        var scale = this.fitScale * this.zoomFactor;
        var overlay = document.createElement('canvas');
        overlay.className = 'ie-lasso-overlay';
        overlay.width = this.workingCanvas.width;
        overlay.height = this.workingCanvas.height;
        overlay.style.position = 'absolute';
        overlay.style.left = '0';
        overlay.style.top = '0';
        overlay.style.width = (this.workingCanvas.width * scale) + 'px';
        overlay.style.height = (this.workingCanvas.height * scale) + 'px';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '5';
        host.appendChild(overlay);
        this.lassoOverlayCanvas = overlay;
      }
      return this.lassoOverlayCanvas.getContext('2d');
    },

    removeLassoOverlay: function () {
      if (this.lassoOverlayCanvas) {
        if (this.lassoOverlayCanvas.parentNode) {
          this.lassoOverlayCanvas.parentNode.removeChild(this.lassoOverlayCanvas);
        }
        this.lassoOverlayCanvas = null;
      }
    },

    // Schedule a single rAF-throttled redraw — extra calls before the frame
    // fires are no-ops, so fast mouse movement can't queue redundant repaints.
    drawLassoPreview: function () {
      if (this.lassoRafId !== null) return;
      this.lassoRafId = requestAnimationFrame(_.bind(function () {
        this.lassoRafId = null;
        this.renderLassoPreview();
      }, this));
    },

    renderLassoPreview: function () {
      var pts = this.lassoPoints;
      if (pts.length < 2) return;
      var ctx = this.getLassoOverlayCtx();
      if (!ctx || !this.lassoOverlayCanvas) return;
      var scale = this.fitScale * this.zoomFactor;
      ctx.clearRect(0, 0, this.lassoOverlayCanvas.width, this.lassoOverlayCanvas.height);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.closePath();
      ctx.fillStyle = 'rgba(59,130,246,0.12)';
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 1.5 / scale;
      ctx.setLineDash([5 / scale, 3 / scale]);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },

    clearLassoPreview: function () {
      if (this.lassoRafId !== null) {
        cancelAnimationFrame(this.lassoRafId);
        this.lassoRafId = null;
      }
      this.removeLassoOverlay();
    },

    cleanupLasso: function () {
      this.lassoDrawing = false;
      this.lassoPoints = [];
      this.clearLassoPreview();
    },

    computeLassoSelection: function () {
      var pts = this.lassoPoints;
      this.lassoPoints = [];
      this.clearLassoPreview();
      if (pts.length < 3) { this.renderWorkingToDisplay(); return; }

      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;

      // Rasterize the closed polygon to build the pixel mask.
      var tmp = document.createElement('canvas');
      tmp.width = w; tmp.height = h;
      var ctx = tmp.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.closePath();
      ctx.fill();

      var data = ctx.getImageData(0, 0, w, h).data;
      var newMask = new Uint8Array(w * h);
      var j;
      for (j = 0; j < newMask.length; j++) {
        if (data[j * 4 + 3] > 0) newMask[j] = 255;
      }

      if (!this.selectionMask || this.lassoMode === 'new') {
        this.selectionMask = newMask;
      } else if (this.lassoMode === 'add') {
        for (j = 0; j < this.selectionMask.length; j++) { if (newMask[j]) this.selectionMask[j] = 255; }
      } else {
        for (j = 0; j < this.selectionMask.length; j++) { if (newMask[j]) this.selectionMask[j] = 0; }
      }
      this.updateSelectionOverlay();
    },

    updateWandPanel: function () {
      var isWand = this.activeTool === 'wand';
      var isLasso = this.activeTool === 'lasso';
      this.ui.wandTitle.toggle(isWand);
      this.ui.lassoTitle.toggle(isLasso);
      this.ui.wandTolRow.toggle(isWand);
      this.ui.wandHintsWand.toggle(isWand);
      this.ui.wandHintsLasso.toggle(isLasso);
      this.ui.wandTolerance.val(this.tolerance);
      this.updateWandActions();
    },

    updateWandActions: function () {
      var active = this.activeTool === 'wand' || this.activeTool === 'lasso';
      var has = active && this.selectionMask !== null;
      if (this.ui.wandActions && this.ui.wandActions.length) this.ui.wandActions.toggle(!!has);
      if (this.ui.wandEmpty && this.ui.wandEmpty.length) {
        this.ui.wandEmpty.toggle(active && !has);
        this.ui.wandEmpty.text(this.activeTool === 'lasso' ?
          _.i18n('imageEditor.lassoEmpty', null, 'Drag on the image to draw a freehand selection.') :
          _.i18n('imageEditor.wandEmpty', null, 'Click on the image to select an area.'));
      }
    },

    /* ------------------------------------------------------------------ */
    /* Adjustments                                                         */
    /* ------------------------------------------------------------------ */

    ensureAdjustBaseline: function () {
      if (!this.adjustBaseline ||
        this.adjustBaseline.width !== this.workingCanvas.width ||
        this.adjustBaseline.height !== this.workingCanvas.height) {
        this.adjustBaseline = this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height);
      }
    },

    applyAdjustments: function () {
      if (!this.workingCtx) return;
      this.ensureAdjustBaseline();
      var src = this.adjustBaseline.data;
      var dst = new Uint8ClampedArray(src.length);
      var b = this.brightness;
      var c = (this.contrast + 100) / 100;
      var intercept = 128 - 128 * c;
      for (var i = 0; i < src.length; i += 4) {
        dst[i]     = src[i]     * c + intercept + b;
        dst[i + 1] = src[i + 1] * c + intercept + b;
        dst[i + 2] = src[i + 2] * c + intercept + b;
        dst[i + 3] = src[i + 3];
      }
      var out = new ImageData(dst, this.adjustBaseline.width, this.adjustBaseline.height);
      this.workingCtx.putImageData(out, 0, 0);
      this.renderWorkingToDisplay();
    },

    onBrightnessChange: function () {
      this.brightness = parseInt(this.ui.brightness.val(), 10) || 0;
      this.ui.brightnessValue.text(this.brightness);
      this.ui.bakeBtn.toggle(this.brightness !== 0 || this.contrast !== 0);
      this.applyAdjustments();
    },

    onContrastChange: function () {
      this.contrast = parseInt(this.ui.contrast.val(), 10) || 0;
      this.ui.contrastValue.text(this.contrast);
      this.ui.bakeBtn.toggle(this.brightness !== 0 || this.contrast !== 0);
      this.applyAdjustments();
    },

    commitAdjustments: function () {
      this.snapshot();
      this.adjustBaseline = null;
      this.brightness = 0;
      this.contrast = 0;
      this.ui.brightness.val(0);
      this.ui.contrast.val(0);
      this.ui.brightnessValue.text('0');
      this.ui.contrastValue.text('0');
      this.ui.bakeBtn.hide();
    },

    /* ------------------------------------------------------------------ */
    /* Rotate / flip                                                       */
    /* ------------------------------------------------------------------ */

    rotate: function (deg) {
      if (!this.workingCtx) return;
      this.snapshot();
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var tmp = document.createElement('canvas');
      tmp.width = h;
      tmp.height = w;
      var ctx = tmp.getContext('2d');
      ctx.translate(tmp.width / 2, tmp.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(this.workingCanvas, -w / 2, -h / 2);
      this.workingCanvas.width = tmp.width;
      this.workingCanvas.height = tmp.height;
      this.workingCtx.drawImage(tmp, 0, 0);
      this.adjustBaseline = null;
      this.fitCanvasToImage();
      this.renderWorkingToDisplay();
    },

    flip: function (axis) {
      if (!this.workingCtx) return;
      this.snapshot();
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      var tmp = document.createElement('canvas');
      tmp.width = w;
      tmp.height = h;
      var ctx = tmp.getContext('2d');
      if (axis === 'h') {
        ctx.translate(w, 0);
        ctx.scale(-1, 1);
      } else {
        ctx.translate(0, h);
        ctx.scale(1, -1);
      }
      ctx.drawImage(this.workingCanvas, 0, 0);
      this.workingCtx.clearRect(0, 0, w, h);
      this.workingCtx.drawImage(tmp, 0, 0);
      this.adjustBaseline = null;
      this.renderWorkingToDisplay();
    },

    /* ------------------------------------------------------------------ */
    /* Crop                                                                */
    /* ------------------------------------------------------------------ */

    startCrop: function () {
      var w = this.workingCanvas.width;
      var h = this.workingCanvas.height;
      this.cropRect = {
        x: Math.round(w / 4),
        y: Math.round(h / 4),
        w: Math.round(w / 2),
        h: Math.round(h / 2)
      };
      this.ui.cropOverlay.show();
      this.ui.cropConfirmBar.css('display', 'flex');
      this.updateCropOverlayCss();
    },

    endCrop: function () {
      this.cropRect = null;
      this.ui.cropOverlay.hide();
      this.ui.cropConfirmBar.hide();
    },

    updateCropOverlayCss: function () {
      if (!this.cropRect) return;
      var scale = this.fitScale * this.zoomFactor;
      // Position relative to canvas-host (canvas's parent).
      var host = this.ui.canvasHost[0];
      var hostRect = host.getBoundingClientRect();
      var canvasRect = this.displayCanvas.getBoundingClientRect();
      var offX = canvasRect.left - hostRect.left;
      var offY = canvasRect.top - hostRect.top;
      this.ui.cropOverlay.css({
        left: (offX + this.cropRect.x * scale) + 'px',
        top: (offY + this.cropRect.y * scale) + 'px',
        width: (this.cropRect.w * scale) + 'px',
        height: (this.cropRect.h * scale) + 'px'
      });
    },

    onCropOverlayMouseDown: function (e) {
      if (!this.cropRect) return;
      e.preventDefault();
      e.stopPropagation();
      var target = $(e.target);
      var handle = target.is('.ie-crop-handle') ? target.data('h') : null;
      this._cropDrag = {
        handle: handle,
        startX: e.clientX,
        startY: e.clientY,
        rect: _.extend({}, this.cropRect)
      };
    },

    onDocMouseMove: function (e) {
      if (this.lassoDrawing) { this.onLassoMouseMove(e); return; }
      if (!this._cropDrag) return;
      var scale = this.fitScale * this.zoomFactor;
      var dx = (e.clientX - this._cropDrag.startX) / scale;
      var dy = (e.clientY - this._cropDrag.startY) / scale;
      var r = _.extend({}, this._cropDrag.rect);
      var canvasW = this.workingCanvas.width;
      var canvasH = this.workingCanvas.height;
      var handle = this._cropDrag.handle;

      if (!handle) {
        // Move
        r.x = Math.max(0, Math.min(canvasW - r.w, r.x + dx));
        r.y = Math.max(0, Math.min(canvasH - r.h, r.y + dy));
      } else {
        if (handle.indexOf('w') >= 0) {
          var newX = Math.max(0, Math.min(r.x + r.w - 8, r.x + dx));
          r.w = r.w + (r.x - newX);
          r.x = newX;
        }
        if (handle.indexOf('e') >= 0) {
          r.w = Math.max(8, Math.min(canvasW - r.x, r.w + dx));
        }
        if (handle.indexOf('n') >= 0) {
          var newY = Math.max(0, Math.min(r.y + r.h - 8, r.y + dy));
          r.h = r.h + (r.y - newY);
          r.y = newY;
        }
        if (handle.indexOf('s') >= 0) {
          r.h = Math.max(8, Math.min(canvasH - r.y, r.h + dy));
        }
      }

      this.cropRect = {
        x: Math.round(r.x),
        y: Math.round(r.y),
        w: Math.round(r.w),
        h: Math.round(r.h)
      };
      this.updateCropOverlayCss();
    },

    onDocMouseUp: function () {
      this._cropDrag = null;
      if (this.lassoDrawing) {
        this.lassoDrawing = false;
        this.computeLassoSelection();
      }
    },

    applyCrop: function () {
      if (!this.cropRect) return;
      var x = Math.max(0, this.cropRect.x);
      var y = Math.max(0, this.cropRect.y);
      var w = Math.min(this.workingCanvas.width - x, this.cropRect.w);
      var h = Math.min(this.workingCanvas.height - y, this.cropRect.h);
      this.endCrop();
      this.activeTool = 'none';
      this.ui.tools.removeClass('active');
      this.ui.cropPanel.hide();
      if (w > 0 && h > 0) {
        this.snapshot();
        var cropped = this.workingCtx.getImageData(x, y, w, h);
        this.workingCanvas.width = w;
        this.workingCanvas.height = h;
        this.workingCtx.putImageData(cropped, 0, 0);
        this.adjustBaseline = null;
        this.fitCanvasToImage();
        this.renderWorkingToDisplay();
      }
    },

    /* ------------------------------------------------------------------ */
    /* Color reduction                                                     */
    /* ------------------------------------------------------------------ */

    extractDominantColors: function (imageData, n) {
      var data = imageData.data;
      var buckets = {};
      for (var i = 0; i < data.length; i += 16) {
        if (data[i + 3] < 16) continue;
        var key = (data[i] & 0xF0) + ',' + (data[i + 1] & 0xF0) + ',' + (data[i + 2] & 0xF0);
        var bucket = buckets[key];
        if (bucket) {
          bucket.count++;
          bucket.sr += data[i];
          bucket.sg += data[i + 1];
          bucket.sb += data[i + 2];
        } else {
          buckets[key] = { count: 1, sr: data[i], sg: data[i + 1], sb: data[i + 2] };
        }
      }
      var list = [];
      for (var k in buckets) {
        if (buckets.hasOwnProperty(k)) list.push(buckets[k]);
      }
      list.sort(function (a, b) { return b.count - a.count; });
      return list.slice(0, n).map(function (e) {
        return { r: Math.round(e.sr / e.count), g: Math.round(e.sg / e.count), b: Math.round(e.sb / e.count) };
      });
    },

    findNearestColor: function (r, g, b, palette) {
      var best = palette[0];
      var bestDist = Infinity;
      for (var i = 0; i < palette.length; i++) {
        var c = palette[i];
        var dr = r - c.r, dg = g - c.g, db = b - c.b;
        var dist = dr * dr + dg * dg + db * db;
        if (dist < bestDist) { bestDist = dist; best = c; }
      }
      return best;
    },

    buildColorReducedImageData: function (n) {
      var src = this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height);
      var palette = this.extractDominantColors(src, n);
      var dst = new ImageData(new Uint8ClampedArray(src.data), src.width, src.height);
      var d = dst.data;
      for (var i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        var c = this.findNearestColor(d[i], d[i + 1], d[i + 2], palette);
        d[i] = c.r; d[i + 1] = c.g; d[i + 2] = c.b;
      }
      return dst;
    },

    countLogoColors: function () {
      var src = this.workingCtx.getImageData(0, 0, this.workingCanvas.width, this.workingCanvas.height);
      var data = src.data;
      var buckets = {};
      var total = 0;
      for (var i = 0; i < data.length; i += 16) {
        if (data[i + 3] < 16) continue;
        total++;
        var key = (data[i] & 0xF0) + ',' + (data[i + 1] & 0xF0) + ',' + (data[i + 2] & 0xF0);
        buckets[key] = (buckets[key] || 0) + 1;
      }
      if (total === 0) return 1;
      var threshold = total * 0.01;
      var n = 0;
      for (var k in buckets) {
        if (buckets.hasOwnProperty(k) && buckets[k] >= threshold) n++;
      }
      return Math.max(1, n);
    },

    generateColorReducePreviews: function () {
      var logoColorCount = this.countLogoColors();
      var maxN = Math.min(5, logoColorCount - 1);
      this.ui.colorReduceList.empty();
      this.ui.colorReduceEmpty.toggle(maxN === 0);
      if (maxN === 0) return;

      this._colorReduceCache = [];
      for (var n = 1; n <= maxN; n++) {
        var imageData = this.buildColorReducedImageData(n);
        var tmp = document.createElement('canvas');
        tmp.width = imageData.width;
        tmp.height = imageData.height;
        tmp.getContext('2d').putImageData(imageData, 0, 0);
        var dataUrl = tmp.toDataURL();
        this._colorReduceCache.push({ n: n, imageData: imageData });
        var label = n === 1 ?
          _.i18n('imageEditor.colorOne', null, '1 color') :
          n + ' ' + _.i18n('imageEditor.colorsPlural', null, 'colors');
        var html = '<div class="ie-cr-item" data-n="' + n + '">' +
          '<div class="ie-cr-thumb"><img src="' + dataUrl + '"/></div>' +
          '<span class="ie-cr-label">' + label + '</span>' +
          '</div>';
        this.ui.colorReduceList.append(html);
      }
      this.ui.colorReduceList.off('click').on('click', '.ie-cr-item', _.bind(function (e) {
        var n = parseInt($(e.currentTarget).data('n'), 10);
        var item = _.findWhere(this._colorReduceCache, { n: n });
        if (item) this.applyColorReduce(item);
      }, this));
    },

    applyColorReduce: function (item) {
      this.snapshot();
      this.workingCtx.putImageData(item.imageData, 0, 0);
      this.adjustBaseline = null;
      this.renderWorkingToDisplay();
      this.setTool('none');
    },

    /* ------------------------------------------------------------------ */
    /* Save / cancel                                                       */
    /* ------------------------------------------------------------------ */

    cancel: function () {
      if (this.windowView) this.windowView.close();
    },

    save: function () {
      if (!this.workingCanvas) return;
      this.$el.addClass('ie-saving');
      var originalName = this.options.fileName || 'image.png';
      var baseName = originalName.replace(/\.[^.]+$/, '');
      var outName = baseName + '-edited-' + Date.now() + '.png';

      this.workingCanvas.toBlob(_.bind(function (blob) {
        if (!blob) {
          this.$el.removeClass('ie-saving');
          return;
        }
        var file;
        try {
          file = new File([blob], outName, { type: 'image/png' });
        } catch (err) {
          // IE/older Safari: File constructor not supported.
          blob.name = outName;
          file = blob;
        }
        if (typeof this.options.onSave === 'function') {
          this.options.onSave(file);
        }
        if (this.windowView) this.windowView.close();
      }, this), 'image/png');
    }
  });

  // Static `open` — opens the editor as a modal Windows.open dialog.
  View.open = function (data) {
    require([
      'windows',
      'imageEditorView'
    ], function (Windows, ViewCtor) {
      Windows.open({
        view: function () { return new ViewCtor(data); },
        id: 'image-editor:' + (data && data.id ? data.id : Date.now()),
        title: (data && data.title) || _.i18n('imageEditor.title', null, 'Image editor'),
        modal: true,
        bodyClass: 'image-editor-body',
        width: '90%',
        height: '90%'
      });
    });
  };

  Module.exports = View;
});

